import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, ChevronLeft, ChevronRight, ShoppingBag, Minus, Plus } from 'lucide-react';
import { absUrl } from '../lib/api';
import { formatMoney } from '../lib/utils';
import { useCart } from '../lib/cart';
import { useNavigate } from 'react-router-dom';

/**
 * Full-screen "Choose Flavour" bottom sheet matching the Retailers UI.
 *
 * Round 2 behavior:
 *  - Dots at the top of the popup represent VARIANTS (size / weight), not flavours.
 *    One dot per variant, active dot in Sri Durga logo red. Visual style unchanged.
 *  - Swipe LEFT/RIGHT on the hero preview OR press left/right arrow keys OR
 *    tap the chevron arrows to switch variants WITHOUT closing the popup.
 *  - Clicking a flavour tile updates the main preview image / name / price to that flavour.
 *  - Cart controls (ADD / +/-) remain on each flavour tile and are click-isolated
 *    from the "select as hero" tap.
 *
 * Props: open, onClose, product (with product.variants[]), initialVariantId
 */
export default function ChooseFlavourSheet({ open, onClose, product, initialVariantId, initialFlavourId }) {
    const { items, addItem, setQty, count, openPreview, confirmSeq } = useCart();
    const navigate = useNavigate();

    const variants = product?.variants || [];
    const startIdx = Math.max(
        0,
        variants.findIndex(v => v.id === initialVariantId),
    );

    const [variantIdx, setVariantIdx] = useState(startIdx === -1 ? 0 : startIdx);
    const [heroFlavourIdx, setHeroFlavourIdx] = useState(0);

    // Sync when the popup is (re)opened with a new starting variant / flavour.
    useEffect(() => {
        if (!open) return;
        const vIdx = startIdx === -1 ? 0 : startIdx;
        setVariantIdx(vIdx);
        // If a specific flavour was requested (e.g. via header live search),
        // preselect it as the hero preview.
        if (initialFlavourId) {
            const flavours = variants[vIdx]?.flavours || [];
            const fIdx = flavours.findIndex(f => f.id === initialFlavourId);
            setHeroFlavourIdx(fIdx >= 0 ? fIdx : 0);
        } else {
            setHeroFlavourIdx(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialVariantId, initialFlavourId, product?.id]);

    // Auto-close when the user "Confirms" from the Cart Preview overlay.
    // The `confirmSeq` counter increments once per Confirm click; watching it
    // here lets us dismiss the flavour popup so the user is returned to the
    // exact homepage scroll position (browser preserves it since no navigation
    // occurred).
    const prevConfirmSeq = useRef(confirmSeq);
    useEffect(() => {
        if (confirmSeq !== prevConfirmSeq.current) {
            prevConfirmSeq.current = confirmSeq;
            if (open) onClose?.();
        }
    }, [confirmSeq, open, onClose]);

    // Reset hero flavour whenever the active variant changes so the preview
    // always starts on the first available flavour of the new size/weight.
    useEffect(() => { setHeroFlavourIdx(0); }, [variantIdx]);

    const variant = variants[variantIdx];
    const flavours = variant?.flavours || [];
    const hero = flavours[heroFlavourIdx] || flavours[0];
    const heroImg = hero?.image_url || product?.image_url;

    const canPrev = variantIdx > 0;
    const canNext = variantIdx < variants.length - 1;

    const goPrev = useCallback(() => {
        setVariantIdx(i => (i > 0 ? i - 1 : i));
    }, []);
    const goNext = useCallback(() => {
        setVariantIdx(i => (i < variants.length - 1 ? i + 1 : i));
    }, [variants.length]);

    // Keyboard arrow-key navigation between variants.
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
            else if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); }
            else if (e.key === 'Escape') { onClose?.(); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, goPrev, goNext, onClose]);

    // Touch swipe support on the hero image container.
    const touchStartRef = useRef(null);
    const onTouchStart = (e) => {
        const t = e.touches?.[0];
        if (!t) return;
        touchStartRef.current = { x: t.clientX, y: t.clientY, t: Date.now() };
    };
    const onTouchEnd = (e) => {
        const start = touchStartRef.current;
        touchStartRef.current = null;
        if (!start) return;
        const t = e.changedTouches?.[0];
        if (!t) return;
        const dx = t.clientX - start.x;
        const dy = t.clientY - start.y;
        const dt = Date.now() - start.t;
        // Horizontal swipe only, must be dominant, > 40px, < 800ms.
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.4 && dt < 800) {
            if (dx < 0) goNext(); else goPrev();
        }
    };

    if (!open || !product || !variant) return null;

    const cartQty = (fid) => {
        const it = items.find(x => x.product_id === product.id && x.variant_id === variant.id && x.flavour_id === fid);
        return it ? it.quantity : 0;
    };

    const addFlavour = (f) => {
        addItem({
            product_id: product.id,
            product_name: product.name,
            variant_id: variant.id,
            size_label: variant.size_label,
            flavour_id: f.id,
            flavour_name: f.name,
            price: f.effective_price || f.price,
            image_url: f.image_url || product.image_url || '',
            quantity: variant.moq || 1,
        });
    };

    const changeQty = (f, delta) => {
        const curr = cartQty(f.id);
        const next = Math.max(0, curr + delta);
        if (curr === 0 && delta > 0) {
            addFlavour(f);
            return;
        }
        setQty(product.id, variant.id, f.id, next);
    };

    return (
        <div className="fixed inset-0 z-50 animate-fade-in" onClick={onClose}>
            <div className="absolute inset-0 bg-black/50" />
            {/* Batch C — Desktop/Laptop popup layout fix (≥1024px only).
                Mobile (default) is UNCHANGED: full-width bottom-sheet growing from
                bottom to `top-8`. Tablet (sm ≤ w < lg): existing centred modal
                (max-w-3xl, max-h ~92vh) — unchanged. Laptop/Desktop (lg and up):
                wider canvas (max-w-5xl, max-h ~88vh) that hosts a two-column
                body (image on the left, flavour list on the right) via the
                inner grid below. Sticky footer keeps the CART / CONFIRM buttons
                always visible inside the viewport. */}
            <div
                className="absolute left-0 right-0 bottom-0 top-8 rounded-t-3xl overflow-hidden shadow-popup flex flex-col animate-slide-up bg-sdf-ivory
                           sm:left-1/2 sm:right-auto sm:bottom-auto sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2
                           sm:w-full sm:max-w-3xl sm:max-h-[92vh] sm:rounded-3xl
                           lg:max-w-5xl lg:max-h-[88vh]"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header — chevrons now switch VARIANTS (not flavours). */}
                <div className="px-4 pt-3 pb-2 flex items-center justify-between border-b border-sdf-line bg-sdf-ivory">
                    <div className="flex items-center gap-2 text-xs font-bold text-sdf-red tracking-widest">
                        <button
                            type="button"
                            onClick={goPrev}
                            disabled={!canPrev}
                            aria-label="Previous size"
                            className="p-1 rounded-full disabled:opacity-30 hover:bg-black/5"
                            data-testid="popup-prev-variant"
                        >
                            <ChevronLeft size={14} />
                        </button>
                        <span data-testid="popup-variant-label">{variant.size_label.toUpperCase()} · CHOOSE FLAVOUR</span>
                        <button
                            type="button"
                            onClick={goNext}
                            disabled={!canNext}
                            aria-label="Next size"
                            className="p-1 rounded-full disabled:opacity-30 hover:bg-black/5"
                            data-testid="popup-next-variant"
                        >
                            <ChevronRight size={14} />
                        </button>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-black/5" data-testid="close-sheet"><X size={20} /></button>
                </div>
                {/* Batch C — Two-column body wrapper.
                    On mobile/tablet: behaves like a normal flex-col with the flavour
                    list scrolling (unchanged). On lg (≥1024px): switches to a
                    grid with a fixed-width left column (image + title/dots)
                    and a scrollable right column (flavour list). The sticky
                    footer sits outside this wrapper so it always stays visible. */}
                <div className="flex-1 min-h-0 flex flex-col overflow-hidden
                                lg:grid lg:grid-cols-[minmax(0,440px)_minmax(0,1fr)] lg:overflow-hidden">
                    {/* LEFT column on lg — title / price / dots / hero image */}
                    <div className="lg:overflow-y-auto lg:border-r lg:border-sdf-line">
                <div className="px-4 pt-2 pb-2">
                    <div className="text-2xl font-extrabold text-sdf-text" data-testid="popup-hero-name">{hero?.name || product.name}</div>
                    <div className="text-lg font-bold text-sdf-red mt-1" data-testid="popup-hero-price">{formatMoney(hero?.effective_price || hero?.price || variant.price)}</div>
                    {hero?.description && (
                        <div className="mt-2 text-xs text-sdf-text-2 leading-relaxed line-clamp-3" data-testid="popup-hero-description">
                            {hero.description}
                        </div>
                    )}
                    {/* Variant (size/weight) pagination dots — one dot per variant. */}
                    <div className="flex gap-1.5 mt-3 items-center" role="tablist" aria-label="Size / weight variants">
                        {variants.map((v, i) => (
                            <button
                                key={v.id}
                                onClick={() => setVariantIdx(i)}
                                aria-label={`Show ${v.size_label}`}
                                aria-selected={i === variantIdx}
                                title={v.size_label}
                                className={`h-1.5 rounded-full transition-all ${i === variantIdx ? 'w-6 bg-sdf-red' : 'w-2 bg-neutral-300 hover:bg-neutral-400'}`}
                                data-testid={`variant-dot-${i}`}
                            />
                        ))}
                    </div>
                </div>
                {/* Hero image — swipe here to change variant */}
                <div
                    className="px-4 lg:pb-3 select-none"
                    onTouchStart={onTouchStart}
                    onTouchEnd={onTouchEnd}
                >
                    <div className="rounded-2xl overflow-hidden bg-neutral-100 aspect-[16/10] lg:aspect-[4/3] lg:max-h-[300px] flex items-center justify-center relative">
                        {heroImg ? (
                            <img
                                src={absUrl(heroImg)}
                                alt={hero?.name || product.name}
                                className="w-full h-full object-cover pointer-events-none"
                                data-testid="popup-hero-image"
                            />
                        ) : (
                            <div className="text-neutral-400 font-semibold">{hero?.name || product.name}</div>
                        )}
                        {/* Edge tap-zones for desktop convenience (invisible) */}
                        {canPrev && (
                            <button
                                type="button"
                                onClick={goPrev}
                                aria-label="Previous size"
                                className="hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 hover:bg-white shadow items-center justify-center"
                                data-testid="popup-hero-prev"
                            >
                                <ChevronLeft size={18} />
                            </button>
                        )}
                        {canNext && (
                            <button
                                type="button"
                                onClick={goNext}
                                aria-label="Next size"
                                className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 hover:bg-white shadow items-center justify-center"
                                data-testid="popup-hero-next"
                            >
                                <ChevronRight size={18} />
                            </button>
                        )}
                    </div>
                </div>
                    </div>

                    {/* RIGHT column on lg — flavour list (scrolls independently). */}
                <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2 lg:min-h-0 lg:h-full lg:flex-none">
                    {flavours.map((f, idx) => {
                        const q = cartQty(f.id);
                        const isHero = idx === heroFlavourIdx;
                        return (
                            <div
                                key={f.id}
                                role="button"
                                tabIndex={0}
                                onClick={() => setHeroFlavourIdx(idx)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setHeroFlavourIdx(idx); } }}
                                className={`card-soft flex items-center gap-3 p-2 pr-3 cursor-pointer transition-shadow ${isHero ? 'ring-2 ring-sdf-red' : 'hover:ring-1 hover:ring-sdf-line'}`}
                                data-testid={`flavour-tile-${f.id}`}
                            >
                                <div className="w-14 h-14 rounded-xl overflow-hidden bg-neutral-100 flex-shrink-0">
                                    {f.image_url ? <img src={absUrl(f.image_url)} alt={f.name} className="w-full h-full object-cover" /> : (
                                        <div className="w-full h-full flex items-center justify-center text-[10px] text-neutral-400 text-center px-1">{product.name}</div>
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-bold text-sdf-text line-clamp-1">{f.name}</div>
                                    <div className="text-xs text-sdf-text-2">{formatMoney(f.effective_price || f.price)}</div>
                                </div>
                                {q > 0 ? (
                                    <FlavourQtyControl
                                        flavour={f}
                                        qty={q}
                                        onChangeQty={(next) => setQty(product.id, variant.id, f.id, Math.max(0, next))}
                                    />
                                ) : (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); addFlavour(f); }}
                                        className="px-4 py-2 rounded-full bg-[#3B0808] text-white text-xs font-bold tracking-wider hover:bg-[#5A1010]"
                                        data-testid={`add-flavour-${f.id}`}
                                    >
                                        ADD
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
                </div>

                {/* Bottom bar */}
                <div className="border-t border-sdf-line bg-white px-3 py-3 flex items-center gap-2 safe-bottom">
                    <button onClick={openPreview} className="flex-1 rounded-full border border-sdf-line py-3 flex items-center justify-center gap-2 font-bold text-sm text-sdf-text" data-testid="sheet-show-cart">
                        <ShoppingBag size={16} /> SHOW CART {count > 0 && <span className="bg-sdf-red text-white text-[10px] rounded-full px-2 py-0.5">{count}</span>}
                    </button>
                    <button onClick={onClose} disabled={count === 0} className="flex-1 rounded-full bg-sdf-red text-white py-3 font-bold text-sm disabled:opacity-60" data-testid="sheet-confirm">CONFIRM</button>
                </div>
            </div>
        </div>
    );
}

/**
 * Quantity control used inside a Choose Flavour tile.
 * Tapping the number turns it into an editable numeric input; the mobile
 * numeric keyboard opens (via inputMode="numeric"). If the field is left
 * empty on blur, the previous quantity is restored. Enter blurs the input.
 */
function FlavourQtyControl({ flavour, qty, onChangeQty }) {
    const [draft, setDraft] = useState(String(qty));
    useEffect(() => { setDraft(String(qty)); }, [qty]);

    const commit = () => {
        const trimmed = draft.trim();
        if (trimmed === '') { setDraft(String(qty)); return; }
        const n = parseInt(trimmed, 10);
        if (isNaN(n) || n < 0) { setDraft(String(qty)); return; }
        if (n !== qty) onChangeQty(n);
    };

    return (
        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
            <button
                onClick={() => onChangeQty(Math.max(0, qty - 1))}
                className="w-8 h-8 rounded-full bg-neutral-200 hover:bg-neutral-300 flex items-center justify-center"
                aria-label="Decrease"
                data-testid={`qty-minus-${flavour.id}`}
            >
                <Minus size={14} />
            </button>
            <input
                type="number"
                inputMode="numeric"
                min="0"
                value={draft}
                onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ''))}
                onBlur={commit}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.currentTarget.blur(); } }}
                onClick={(e) => { e.stopPropagation(); e.currentTarget.select(); }}
                className="w-11 h-8 border border-sdf-line rounded-md text-center text-sm font-bold outline-none focus:ring-2 focus:ring-sdf-red bg-white"
                aria-label="Quantity"
                data-testid={`qty-input-${flavour.id}`}
            />
            <button
                onClick={() => onChangeQty(qty + 1)}
                className="w-8 h-8 rounded-full bg-sdf-red text-white hover:bg-sdf-red-hover flex items-center justify-center"
                aria-label="Increase"
                data-testid={`qty-plus-${flavour.id}`}
            >
                <Plus size={14} />
            </button>
        </div>
    );
}
